import { chain } from 'lodash';
import produce from 'immer';
import isEqual from 'react-fast-compare';
import { DatasetStatus } from '@retro-ui/api-contract';

import { defaultProcessingState } from '../utils';

import {
  STARTING_MATERIAL_CREATE_UPLOAD_START,
  STARTING_MATERIAL_CREATE_UPLOAD_FULFILL,
  STARTING_MATERIAL_CREATE_UPLOAD_REJECT,
  STARTING_MATERIAL_UPLOAD_PART_START,
  STARTING_MATERIAL_UPLOAD_PART_FULFILL,
  STARTING_MATERIAL_COMPLETE_UPLOAD_START,
  STARTING_MATERIAL_COMPLETE_UPLOAD_FULFILL,
  STARTING_MATERIAL_COMPLETE_UPLOAD_REJECT,
  STARTING_MATERIAL_FIND_DATASETS_START,
  STARTING_MATERIAL_FIND_DATASETS_FULFILL,
  STARTING_MATERIAL_FIND_DATASETS_REJECT,
  STARTING_MATERIAL_RESUME_UPLOAD_START,
  STARTING_MATERIAL_RESUME_UPLOAD_FULFILL,
  STARTING_MATERIAL_RESUME_UPLOAD_REJECT,
  STARTING_MATERIAL_REMOVE_DATASET_START,
  STARTING_MATERIAL_REMOVE_DATASET_FULFILL,
  STARTING_MATERIAL_REMOVE_DATASET_REJECT,
  STARTING_MATERIAL_POLL_FULFILL,
  STARTING_MATERIAL_UPLOAD_PART_REJECT,
} from './starting-material.actions';
import {
  DatasetReducerDataItem,
  StartingMaterialDatasetItem,
  StartingMaterialReducer,
  UploadedPart,
} from './starting-material.types';

const defaultState: StartingMaterialReducer = {
  dataset: {
    find: {
      ...defaultProcessingState(),
      initialized: false,
    },
    create: defaultProcessingState(),
    resume: defaultProcessingState(),
    complete: defaultProcessingState(),
    remove: defaultProcessingState(),
    data: {},
  },
};

export default produce((draft, action = {}) => {
  switch (action.type) {
    case STARTING_MATERIAL_FIND_DATASETS_START: {
      ++draft.dataset.find.processing;
      return draft;
    }

    case STARTING_MATERIAL_FIND_DATASETS_FULFILL: {
      --draft.dataset.find.processing;
      draft.dataset.find.initialized = true;
      draft.dataset.find.error = null;
      draft.dataset.data = chain(action.payload)
        .map<DatasetReducerDataItem>((item: DatasetReducerDataItem) => {
          const { id, status, uploadedParts = [] } = item;

          item.size = Number(item.size);

          if (
            status === DatasetStatus.INCOMPLETE &&
            draft.dataset.data[id]?.status === DatasetStatus.SENDING
          ) {
            item.status = DatasetStatus.SENDING;
          }

          if (status === DatasetStatus.INCOMPLETE) {
            const uploadedSize = uploadedParts.reduce((acc: number, part: UploadedPart) => {
              acc += part.size;
              return acc;
            }, 0);

            const draftUploadedSize = draft.dataset.data[id]?.uploadedSize ?? 0;
            item.uploadedSize = Math.max(uploadedSize, draftUploadedSize);
          }

          return item;
        })
        .keyBy('id')
        .thru(newData => ({ ...draft.dataset.data, ...newData }))
        .value();

      return draft;
    }

    case STARTING_MATERIAL_FIND_DATASETS_REJECT: {
      --draft.dataset.find.processing;
      draft.dataset.find.error = action.payload;
      return draft;
    }

    case STARTING_MATERIAL_POLL_FULFILL: {
      const newDatasets = chain(action.payload)
        .map<DatasetReducerDataItem>((item: DatasetReducerDataItem) => {
          const { id, status, uploadedParts = [] } = item;

          item.size = Number(item.size);

          if (status === DatasetStatus.INCOMPLETE) {
            const uploadedSize = uploadedParts.reduce((acc: number, part: UploadedPart) => {
              acc += part.size;
              return acc;
            }, 0);

            const draftUploadedSize = draft.dataset.data[id]?.uploadedSize ?? 0;
            item.uploadedSize = Math.max(uploadedSize, draftUploadedSize);
          }

          return item;
        })
        .keyBy('id')
        .thru(newData => ({ ...draft.dataset.data, ...newData }))
        .value();

      if (isEqual(draft.dataset.data, newDatasets)) {
        return draft;
      }
      draft.dataset.data = newDatasets;
      return draft;
    }

    case STARTING_MATERIAL_CREATE_UPLOAD_START: {
      ++draft.dataset.create.processing;
      return draft;
    }

    case STARTING_MATERIAL_CREATE_UPLOAD_FULFILL: {
      const { id, size } = action.payload;

      --draft.dataset.create.processing;
      draft.dataset.create.error = null;
      draft.dataset.data[id] = {
        ...action.payload,
        size: Number(size),
        uploadedSize: 0,
        parts: {},
        status: DatasetStatus.SENDING,
      };

      return draft;
    }

    case STARTING_MATERIAL_CREATE_UPLOAD_REJECT: {
      --draft.dataset.create.processing;
      draft.dataset.create.error = action.payload;
      return draft;
    }

    case STARTING_MATERIAL_RESUME_UPLOAD_START: {
      ++draft.dataset.resume.processing;
      return draft;
    }

    case STARTING_MATERIAL_RESUME_UPLOAD_FULFILL: {
      const { id, size, uploadedParts } = action.payload;

      --draft.dataset.resume.processing;
      draft.dataset.resume.error = null;
      draft.dataset.data[id] = {
        ...action.payload,
        size: Number(size),
        uploadedSize: uploadedParts.reduce((acc: number, part: UploadedPart) => {
          acc += part.size;
          return acc;
        }, 0),
        parts: uploadedParts.reduce(
          (acc: StartingMaterialDatasetItem['parts'], part: UploadedPart) => {
            const { partNumber, etag } = part;
            acc[partNumber] = etag;
            return acc;
          },
          {}
        ),
      };

      return draft;
    }

    case STARTING_MATERIAL_RESUME_UPLOAD_REJECT: {
      --draft.dataset.resume.processing;
      draft.dataset.resume.error = action.payload;
      return draft;
    }

    case STARTING_MATERIAL_COMPLETE_UPLOAD_START: {
      ++draft.dataset.complete.processing;
      return draft;
    }

    case STARTING_MATERIAL_COMPLETE_UPLOAD_FULFILL: {
      const { id, size } = action.payload;

      --draft.dataset.complete.processing;
      draft.dataset.complete.error = null;
      draft.dataset.data[id] = {
        ...action.payload,
        size: Number(size),
        uploadedSize: Number(size),
      };

      return draft;
    }

    case STARTING_MATERIAL_COMPLETE_UPLOAD_REJECT: {
      --draft.dataset.complete.processing;
      draft.dataset.complete.error = action.payload;
      return draft;
    }

    case STARTING_MATERIAL_REMOVE_DATASET_START: {
      ++draft.dataset.remove.processing;
      return draft;
    }

    case STARTING_MATERIAL_REMOVE_DATASET_FULFILL: {
      --draft.dataset.remove.processing;
      draft.dataset.remove.error = null;

      const datasetsIds = action.payload.map(dataset => dataset.id);
      for (const id of datasetsIds) {
        if (
          [DatasetStatus.SENDING, DatasetStatus.PROCESSING].includes(draft.dataset.data[id].status)
        ) {
          draft.dataset.data[id].status = DatasetStatus.CANCELED;
        } else {
          draft.dataset.data[id].status = DatasetStatus.REMOVED;
        }
      }

      return draft;
    }

    case STARTING_MATERIAL_REMOVE_DATASET_REJECT: {
      --draft.dataset.remove.processing;
      draft.dataset.remove.error = action.payload;
      return draft;
    }

    case STARTING_MATERIAL_UPLOAD_PART_START: {
      const { id } = action.payload;

      draft.dataset.data[id].status = DatasetStatus.SENDING;

      return draft;
    }

    case STARTING_MATERIAL_UPLOAD_PART_FULFILL: {
      const { id, partNumber, etag, size } = action.payload;

      if ([DatasetStatus.CANCELED, DatasetStatus.REMOVED].includes(draft.dataset.data[id].status)) {
        return draft;
      }

      const { parts } = draft.dataset.data[id];

      draft.dataset.data[id].parts = {
        ...parts,
        [partNumber]: etag,
      };
      draft.dataset.data[id].uploadedSize += size;

      return draft;
    }

    case STARTING_MATERIAL_UPLOAD_PART_REJECT: {
      const { id } = action.payload;

      draft.dataset.data[id].status = DatasetStatus.CANCELED;

      return draft;
    }

    default:
      return draft;
  }
}, defaultState);
